import React from 'react'
import { Modal } from 'common/styleguide/Modal'
import { CTA } from 'common/styleguide/CTA'
import { FancyCheckmark } from 'common/FancyCheckmark'
import { LoginModal } from 'common/styleguide/LoginModal'
import { CircleSpinner } from 'common/spinners/CircleSpinner'
import { Icon } from 'common/Icon'
import styles from './PairDevicePage.scss'
import { getCurrentUser } from 'lib/utils'
import fetchJSON from 'lib/fetchJSON'
import { browserHistory } from 'react-router'
import _ from 'lodash'
import unknownDevice from './unknown_device.svg'

// These values are extracted from the devices.xml file that is distributed with the connect IQ SDK
//
// TODO - write a script to extract ALL of these from devices.xml
// TODO - make sure the part number doesn't change based on the firmware version!
// TODO - confirm with Garmin that we can use these images
// and rehost them on our CDN
const GARMIN_DEVICES = {
  '006-B2909-00': {
    friendlyName: 'Garmin Edge 130',
    deviceImage: require('./device_imgs/edge130.jpg')
  },
  '006-B2067-00': {
    friendlyName: 'Garmin Edge 520',
    deviceImage: require('./device_imgs/edge520.jpg')
  },
  '006-B2713-00': {
    friendlyName: 'Garmin Edge 1030',
    deviceImage: require('./device_imgs/edge1030.jpg')
  },
  '006-B2530-00': {
    friendlyName: 'Garmin Edge 820',
    deviceImage: require('./device_imgs/edge820.jpg')
  },
  '006-B1836-00': {
    friendlyName: 'Garmin Edge 1000',
    deviceImage: require('./device_imgs/edge1000.jpg')
  },
  '006-B3121-00': {
    friendlyName: 'Garmin Edge 530',
    deviceImage: require('./device_imgs/edge530.jpg')
  },
  '006-B3122-00': {
    friendlyName: 'Garmin Edge 830',
    deviceImage: require('./device_imgs/edge830.jpg')
  }
}

/*
All of the initial states that we have to deal with
1. User is logged out and we don't have a device code from the URL
2. User is logged out and we do have a device code from the URL
3. User is logged in and we don't have a device code from the URL
4. User is logged in and we do have a device code from the URL
*/

/*
We've got three basic pages:
1. Login screen
2. Enter code screen
3. Code confirmation screen
*/

// const APIKEY = "f121c33d" // rwgps-web.local
// const APIKEY = 'bd408eae' // dev1.ridewithgps.com
const APIKEY = 'b9dca593' // ridewithgps.com

export class PairDevicePage extends React.PureComponent {
  static defaultProps = {
    currentUser: getCurrentUser()
  }

  state = {
    shortcodeInfo: null,
    pairingState: null
  }

  componentDidMount () {
    if (this.props.params.shortcode) {
      this.lookupShortcode()
    }
  }

  componentDidUpdate (prevProps) {
    const shortcodeChanged = prevProps.params.shortcode !== this.props.params.shortcode
    const shortcodeInfoInvalid = _.get(this.state, 'shortcodeInfo.shortcode') !== this.props.params.shortcode
    if (shortcodeChanged && shortcodeInfoInvalid) {
      this.lookupShortcode()
    }
  }

  lookupShortcode = () => {
    const shortcode = this.props.params.shortcode.toUpperCase()
    fetchJSON(`/shortcodes/${shortcode}?apikey=${APIKEY}`)
      .then(({ shortcode: shortcodeInfo }) => this.setState({ shortcodeInfo }))
      .catch(({ errors }) => {
        if (errors[0] && errors[0].error_code === 'not_found') {
          this.setState({ shortcodeInfo: 'not_found' })
        }
      })
  }

  authorizeShortcode = () => {
    const shortcode = this.props.params.shortcode.toUpperCase()
    fetchJSON(`/shortcodes/associate`, { shortcode, apikey: APIKEY })
      .then(() => this.setState({ pairingState: 'pending' }))

    this.beginPollingPairingState()
  }

  beginPollingPairingState = () => {
    const POLL_FREQ = 3000
    const shortcode = this.props.params.shortcode
    const poll = () => {
      fetchJSON(`/shortcodes/${shortcode}?apikey=${APIKEY}`)
        .then(() => setTimeout(poll, POLL_FREQ))
        .catch(({ errors }) => {
          // This is a little bit of an odd data flow.
          // Basically, once we're at this point in the code, we know that
          // at one point, there was a valid record in the database for this
          // shortcode. Right now, the backend deletes shortcode exchange
          // records once they're consumed by a Connect IQ client, so we use
          // the 404 Not Found as a signal that the CiQ device was able to
          // successfully exchange the shortcode for an auth_token.
          //
          // This is certaintly something that can be improved upon in the
          // future, but for now it's simple, fairly robust, and sensible
          // from a backend-schema perspective (just not a frontend success
          // semantics perspective).
          if (errors[0].error_code === 'not_found') {
            this.setState({ pairingState: 'confirmed' })
          } else {
            setTimeout(poll, POLL_FREQ)
          }
        })
    }
    poll()
  }

  onCodeConfirmed = (shortcode, shortcodeInfo) => {
    this.setState({ shortcodeInfo }, () => {
      browserHistory.push(`/iq/${shortcode}`)
    })
  }

  reset = (e) => {
    e && e.preventDefault()
    browserHistory.push('/iq/')
    this.setState({
      shortcodeInfo: null,
      pairingState: null
    })
  }

  render () {
    const { currentUser, params } = this.props
    const { shortcodeInfo, pairingState } = this.state

    const recognizedGarmin = GARMIN_DEVICES[_.get(shortcodeInfo, 'device_name')]

    const deviceName = recognizedGarmin
      ? recognizedGarmin.friendlyName
      : _.get(shortcodeInfo, 'device_name')

    const deviceImg = recognizedGarmin
      ? (recognizedGarmin.deviceImage || unknownDevice)
      : unknownDevice

    if (!currentUser.id) {
      return <RequestLogin />
    }

    if (pairingState === 'pending') {
      return (
        <div className={styles.PairDevicePage}>
          <Modal
            title='Pair Device'
            className={styles.Modal}
            showCloseButton={false}>
            <div style={{ textAlign: 'center' }}>
              {deviceImg && <img src={deviceImg} height='200' style={{ display: 'block', margin: '0 auto' }} />}
              {!deviceImg && <img src={unknownDevice} height='200' style={{ display: 'block', margin: '0 auto' }} />}
              <CircleSpinner size={30} />
              <p>Syncing with your <strong>{deviceName}</strong></p>
            </div>
          </Modal>
        </div>
      )
    }

    if (pairingState === 'confirmed') {
      return (
        <div className={styles.PairDevicePage}>
          <Modal
            title='Pair Device'
            className={styles.Modal}
            showCloseButton={false}>
            <div style={{ textAlign: 'center' }}>
              {deviceImg && <img src={deviceImg} height='200' style={{ display: 'block', margin: '0 auto' }} />}
              {!deviceImg && <img src={unknownDevice} height='200' style={{ display: 'block', margin: '0 auto' }} />}
              <FancyCheckmark />
              <p>Your <strong>{deviceName}</strong> is now paired with your account</p>
              <p><CTA margins={false} href='/'>Go to your profile</CTA></p>
              <p><a href='/iq' onClick={this.reset}>Pair another device</a></p>
            </div>
          </Modal>
        </div>
      )
    }

    if (params.shortcode) {
      switch (shortcodeInfo) {
        case null: {
          return <Layout>
            <div style={{ height: '30vh', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '10vh' }}>
              <div>
                <CircleSpinner centered size={25} />
                <p>Loading Device Info</p>
              </div>
            </div>
          </Layout>
        }
        case 'not_found': {
          return <Layout>
            <div style={{ height: '250px', display: 'flex', alignItems: 'center', marginBottom: '50px' }}>
              <div>
                <p style={{ textAlign: 'center' }}><Icon i='ios-cube-outline' size={40} /></p>
                <p style={{ textAlign: 'center' }}>We couldn't find a record of that device code in our system.</p>
                <p style={{ textAlign: 'center' }}><a href='/iq' onClick={this.reset}>&laquo; Enter a different code</a></p>
              </div>
            </div>
          </Layout>
        }
        default: {
          return <Layout>
            <div>
              {deviceImg && <img src={deviceImg} height='200' style={{ display: 'block', margin: '0 auto' }} />}
              {!deviceImg && <img src={unknownDevice} height='200' style={{ display: 'block', margin: '0 auto' }} />}
              <p>
                Do you want to authorize the device
                <br /><strong>{deviceName}</strong>
                <br />to access your account?
              </p>
              <CTA margins={false} onClick={this.authorizeShortcode}>Confirm</CTA>
              <CTA link margins={false} href='/iq'>Cancel</CTA>{/* I'm using a hard link (href) as a poor man's reset button for this component */}
            </div>
          </Layout>
        }
      }
    }

    return (
      <Layout>
        <p>
          Please launch the RWGPS Connect IQ
          <br />app on your Garmin device
        </p>
        <p>
          Enter the 6-character device
          <br />code displayed by the app
        </p>
        <ShortcodeForm onCodeConfirmed={this.onCodeConfirmed} />
      </Layout>
    )
  }
}

const Layout = ({ children }) => (
  <div className={styles.PairDevicePage}>
    <Modal
      title='Pair Device'
      className={styles.Modal}
      showCloseButton={false}>
      <div className={styles.container}>
        {children}
      </div>
    </Modal>
  </div>
)

const RequestLogin = () => (
  <div className={styles.PairDevicePage}>
    <div className={styles.Login}>
      <LoginModal
        title='Please log in to pair your device'
        showCloseButton={false} />
    </div>
  </div>
)

class ShortcodeForm extends React.PureComponent {
  state = {
    formState: 'entry',
    error: null,
    shortcode: ''
  }

  onSubmitCode = (e) => {
    e.preventDefault()
    this.setState({ formState: 'loading' })
    this.validate()
      .then(({ shortcode: shortcodeInfo }) => this.props.onCodeConfirmed(this.state.shortcode, shortcodeInfo))
      .catch(() => {}) // catch is noop, the state changes are handled inside validate()
  }

  validate = () => {
    const { shortcode } = this.state
    if (!shortcode) {
      const message = 'Please enter a shortcode'
      this.setState({ error: message })
      return Promise.reject(Error(message))
    }
    if (shortcode.length !== 6) {
      const message = 'Shortcode should be 6 characters long'
      this.setState({ error: message })
      return Promise.reject(Error(message))
    }
    return fetchJSON(`/shortcodes/${shortcode}?apikey=${APIKEY}`)
      .catch((res) => {
        if (res.errors) {
          const message = res.errors[0].error_message
          this.setState({ error: message })
          return Promise.reject(Error(message))
        } else {
          // this should never happen with APIv3 error handling (a 4xx response)
          // should always return a valid JSON body containing a non-empty
          // errors array, but it doesn't hurt to be careful until we have better
          // test coverage in place
          const message = 'Oops! Something went wrong with your request. Please try again.'
          this.setState({ error: message })
          return Promise.reject(Error(message))
        }
      })
  }

  onChange = (e) => {
    this.setState({ shortcode: e.target.value.trim().toUpperCase().slice(0, 6) })
  }

  render () {
    const { error, shortcode } = this.state
    return (
      <form onSubmit={this.onSubmitCode}>
        <label>
          Device code
        </label>
        <input type='text' value={shortcode} placeholder='example: AQ4DLE' onChange={this.onChange} />
        {error && <p className={styles.error}>{error}</p>}
        <CTA button type='submit'>Submit</CTA>
      </form>
    )
  }
}
